<div class="external-integration-manager">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">
            <i class="fa-solid fa-plug mr-2"></i>
            Externe Integrationen
        </h3>
        <span class="text-sm text-gray-500" *ngIf="campaignName">
            Kampagne: {{ campaignName }}
        </span>
    </div>

    <!-- Messages -->
    <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <div class="flex justify-between items-center">
            <span><i class="fa-solid fa-exclamation-circle mr-2"></i>{{ error }}</span>
            <button (click)="clearMessages()" class="text-red-700 hover:text-red-900">
                <i class="fa-solid fa-times"></i>
            </button>
        </div>
    </div>

    <div *ngIf="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
        <div class="flex justify-between items-center">
            <span><i class="fa-solid fa-check-circle mr-2"></i>{{ successMessage }}</span>
            <button (click)="clearMessages()" class="text-green-700 hover:text-green-900">
                <i class="fa-solid fa-times"></i>
            </button>
        </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-4">
        <nav class="-mb-px flex space-x-8">
            <button
                (click)="onTabChange('overview')"
                [class]="activeTab === 'overview' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                class="py-2 px-1 border-b-2 font-medium text-sm">
                <i class="fa-solid fa-chart-pie mr-1"></i> Übersicht
            </button>
            <button
                (click)="onTabChange('configure')"
                [class]="activeTab === 'configure' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                class="py-2 px-1 border-b-2 font-medium text-sm">
                <i class="fa-solid fa-cog mr-1"></i> Konfigurieren
            </button>
            <button
                (click)="onTabChange('history')"
                [class]="activeTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                class="py-2 px-1 border-b-2 font-medium text-sm">
                <i class="fa-solid fa-clock-rotate-left mr-1"></i> Verlauf
            </button>
        </nav>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="text-center py-8">
        <i class="fa-solid fa-spinner fa-spin text-2xl text-blue-500"></i>
        <p class="mt-2 text-gray-500">Wird geladen...</p>
    </div>

    <!-- Overview Tab -->
    <div *ngIf="activeTab === 'overview' && !loading">
        <div *ngIf="syncConfigurations.length === 0" class="text-center py-8 text-gray-500">
            <i class="fa-solid fa-plug text-4xl mb-3"></i>
            <p>Keine Integrationen für diese Kampagne konfiguriert.</p>
            <p class="text-sm">Wechseln Sie zum Tab „Konfigurieren", um eine Integration hinzuzufügen.</p>
        </div>

        <!-- Sync Configuration Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div *ngFor="let config of syncConfigurations"
                 class="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <!-- Integration Header -->
                <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center">
                        <span [class]="'rounded-full p-2 mr-3 ' + getIntegrationTypeColor(config.integrationType)">
                            <i [class]="getIntegrationTypeIcon(config.integrationType)"></i>
                        </span>
                        <div>
                            <h4 class="font-semibold">{{ config.integrationName }}</h4>
                            <span class="text-sm text-gray-500">{{ config.integrationType }}</span>
                        </div>
                    </div>
                    <span [class]="'px-2 py-1 rounded text-xs font-medium ' + getSyncStatusClass(config.syncStatus)">
                        <i [class]="getSyncStatusIcon(config.syncStatus) + ' mr-1'"></i>
                        {{ config.syncStatus }}
                    </span>
                </div>

                <!-- Sync Direction -->
                <div class="flex items-center text-sm text-gray-600 mb-3">
                    <i [class]="getSyncDirectionIcon(config.syncDirection) + ' mr-2'"></i>
                    <span>{{ config.syncDirection }}</span>
                    <span *ngIf="config.isAutoSyncEnabled" class="ml-2 text-green-600">
                        <i class="fa-solid fa-sync mr-1"></i>Auto-Synchronisierung aktiviert
                    </span>
                </div>

                <!-- Progress Bar -->
                <div class="mb-3">
                    <div class="flex justify-between text-sm mb-1">
                        <span>Synchronisierungsfortschritt</span>
                        <span>{{ config.syncedMembers }} / {{ config.totalMembers }}</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-2">
                        <div class="bg-blue-600 h-2 rounded-full"
                             [style.width.%]="getSyncProgress(config)"></div>
                    </div>
                    <div *ngIf="hasFailedMembers(config)" class="text-xs text-red-600 mt-1">
                        <i class="fa-solid fa-exclamation-triangle mr-1"></i>
                        {{ config.failedMembers }} Mitglieder konnten nicht synchronisiert werden
                    </div>
                </div>

                <!-- Last Sync Info -->
                <div class="text-sm text-gray-500 mb-3">
                    <div *ngIf="config.lastSyncedAt">
                        <i class="fa-solid fa-clock mr-1"></i>
                        Letzte Synchronisierung: {{ formatDate(config.lastSyncedAt) }}
                    </div>
                    <div *ngIf="config.externalCampaignId">
                        <i class="fa-solid fa-link mr-1"></i>
                        Externe ID: {{ config.externalCampaignId }}
                    </div>
                </div>

                <!-- Actions -->
                <div class="flex gap-2">
                    <button
                        (click)="triggerSync(config.campaignExternalSyncId, false)"
                        [disabled]="syncing"
                        class="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                        <i [class]="syncing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-sync'"></i>
                        Jetzt synchronisieren
                    </button>
                    <button
                        (click)="triggerSync(config.campaignExternalSyncId, true)"
                        [disabled]="syncing"
                        class="bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 disabled:opacity-50"
                        title="Vollständige Synchronisierung">
                        <i class="fa-solid fa-rotate"></i>
                    </button>
                    <button
                        *ngIf="hasFailedMembers(config)"
                        (click)="retryFailedMembers(config.campaignExternalSyncId)"
                        [disabled]="syncing"
                        class="bg-yellow-100 text-yellow-700 px-3 py-2 rounded text-sm hover:bg-yellow-200 disabled:opacity-50"
                        title="Fehlgeschlagene erneut versuchen">
                        <i class="fa-solid fa-redo"></i>
                    </button>
                    <button
                        (click)="toggleAutoSync(config.campaignExternalSyncId, !config.isAutoSyncEnabled)"
                        [class]="config.isAutoSyncEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'"
                        class="px-3 py-2 rounded text-sm hover:opacity-80"
                        [title]="config.isAutoSyncEnabled ? 'Auto-Synchronisierung deaktivieren' : 'Auto-Synchronisierung aktivieren'">
                        <i class="fa-solid fa-clock"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Configure Tab -->
    <div *ngIf="activeTab === 'configure' && !loading">
        <div class="max-w-lg">
            <h4 class="font-medium mb-4">Neue Integration hinzufügen</h4>

            <!-- Select Integration -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    Integration auswählen
                </label>
                <select
                    [(ngModel)]="selectedIntegrationId"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option [ngValue]="undefined">-- Integration auswählen --</option>
                    <option *ngFor="let integration of availableIntegrations" [ngValue]="integration.id">
                        {{ integration.name }} ({{ integration.integrationType }})
                    </option>
                </select>
            </div>

            <!-- Test Connection Button -->
            <div class="mb-4" *ngIf="selectedIntegrationId">
                <button
                    (click)="testIntegration(selectedIntegrationId)"
                    [disabled]="testing"
                    class="text-blue-600 hover:text-blue-800 text-sm">
                    <i [class]="testing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-plug'"></i>
                    Verbindung testen
                </button>
            </div>

            <!-- Sync Direction -->
            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    Synchronisierungsrichtung
                </label>
                <select
                    [(ngModel)]="configSyncDirection"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option *ngFor="let option of syncDirectionOptions" [ngValue]="option.value">
                        {{ option.label }}
                    </option>
                </select>
            </div>

            <!-- Auto Sync -->
            <div class="mb-4">
                <label class="flex items-center">
                    <input
                        type="checkbox"
                        [(ngModel)]="configAutoSync"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2">
                    <span class="text-sm text-gray-700">Automatische Synchronisierung bei Kampagnenaktivierung</span>
                </label>
            </div>

            <!-- Submit Button -->
            <button
                (click)="configureSync()"
                [disabled]="!selectedIntegrationId || loading"
                class="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                <i class="fa-solid fa-plus mr-2"></i>
                Synchronisierung konfigurieren
            </button>
        </div>

        <!-- Available Integrations List -->
        <div class="mt-8">
            <h4 class="font-medium mb-4">Verfügbare Integrationen</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div *ngFor="let integration of availableIntegrations"
                     class="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div class="flex items-center mb-2">
                        <span [class]="'rounded-full p-2 mr-3 ' + getIntegrationTypeColor(integration.integrationType)">
                            <i [class]="getIntegrationTypeIcon(integration.integrationType)"></i>
                        </span>
                        <div>
                            <h5 class="font-medium">{{ integration.name }}</h5>
                            <span class="text-xs text-gray-500">{{ integration.integrationType }}</span>
                        </div>
                    </div>
                    <div class="text-sm text-gray-600" *ngIf="integration.description">
                        {{ integration.description }}
                    </div>
                    <div class="mt-2 flex items-center text-xs">
                        <span *ngIf="integration.lastTestSuccessful"
                              class="text-green-600">
                            <i class="fa-solid fa-check-circle mr-1"></i>Verbunden
                        </span>
                        <span *ngIf="integration.lastTestSuccessful === false"
                              class="text-red-600">
                            <i class="fa-solid fa-times-circle mr-1"></i>Verbindung fehlgeschlagen
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- History Tab -->
    <div *ngIf="activeTab === 'history' && !loading">
        <!-- Sync Selector -->
        <div class="mb-4" *ngIf="syncConfigurations.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">
                Integration auswählen
            </label>
            <select
                [(ngModel)]="selectedSyncId"
                (ngModelChange)="onSyncConfigChange($event)"
                class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option *ngFor="let config of syncConfigurations" [ngValue]="config.campaignExternalSyncId">
                    {{ config.integrationName }}
                </option>
            </select>
        </div>

        <div *ngIf="syncHistory.length === 0" class="text-center py-8 text-gray-500">
            <i class="fa-solid fa-clock-rotate-left text-4xl mb-3"></i>
            <p>Kein Synchronisierungsverlauf verfügbar.</p>
        </div>

        <!-- History Table -->
        <div *ngIf="syncHistory.length > 0" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gestartet</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Richtung</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dauer</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Datensätze</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr *ngFor="let execution of syncHistory" class="hover:bg-gray-50">
                        <td class="px-4 py-3 text-sm">{{ formatDate(execution.startedAt) }}</td>
                        <td class="px-4 py-3 text-sm">
                            <i [class]="getSyncDirectionIcon(execution.syncDirection) + ' mr-1'"></i>
                            {{ execution.syncDirection }}
                        </td>
                        <td class="px-4 py-3">
                            <span [class]="'px-2 py-1 rounded text-xs font-medium ' + getSyncStatusClass(execution.syncStatus)">
                                <i [class]="getSyncStatusIcon(execution.syncStatus) + ' mr-1'"></i>
                                {{ execution.syncStatus }}
                            </span>
                        </td>
                        <td class="px-4 py-3 text-sm">{{ formatDuration(execution.durationMs) }}</td>
                        <td class="px-4 py-3 text-sm">
                            <span class="text-green-600" *ngIf="execution.recordsCreated > 0">
                                +{{ execution.recordsCreated }}
                            </span>
                            <span class="text-blue-600 ml-2" *ngIf="execution.recordsUpdated > 0">
                                ↻{{ execution.recordsUpdated }}
                            </span>
                            <span class="text-red-600 ml-2" *ngIf="execution.recordsFailed > 0">
                                ✗{{ execution.recordsFailed }}
                            </span>
                            <span class="text-gray-500" *ngIf="execution.recordsProcessed === 0">-</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</div>
